<template>
  <section>
    <header>
      <h1 class="page-header">Register</h1>
    </header>
    <errors-alert
      v-bind:errors="errorsFromBackend"
      v-on:clear="clearAlert()"
    />
    <section class="alert alert-info">
      <p>No credit card required right now. You'll automatically be entered into the 14 day free trial after you register. After that, you can choose to purchase the basic version for $25, or the premium version for $75.</p>
      <hr>
      <p>See the <router-link to="/faq">FAQ page</router-link> for more info, and <router-link to="/contact">get in touch</router-link> if you have any questions at all.</p>
    </section>
    <br>
    <form>
      <section class="form-group" v-bind:class="{ 'has-error': (submitAttempted && emailErrorMessage) }">
        <label class="control-label">
          Email
          <input
            v-model="email"
            required
            type="email"
            class="form-control input-lg"
            autofocus
          >
        </label>
        <p v-if="submitAttempted && emailErrorMessage" class="help-block">
          {{ emailErrorMessage }}
        </p>
      </section>
      <section class="form-group" v-bind:class="{ 'has-error': (submitAttempted && passwordErrorMessage) }">
        <label class="control-label">
          Password
          <input
            v-model="password"
            required
            type="text"
            class="form-control input-lg"
          >
        </label>
        <p class="help-block">
          Must be at least eight characters <span v-if="password.length < 8">ℹ️</span><span v-else>✅</span>.
          <span v-on:click.prevent="xkcdVisible = !xkcdVisible" class="xkcd-toggle">
            Inspiration <span v-if="!xkcdVisible">▾</span><span v-else>▴</span>
          </span>
        </p>
        <p v-if="submitAttempted && passwordErrorMessage" class="help-block">
          {{ passwordErrorMessage }}
        </p>
        <img v-if="xkcdVisible" src="/static/xkcd-password-strength.png">
      </section>
      <div class="checkbox">
        <label>
          <input v-model="emailOptIn" type="checkbox"> Opt in to receiving infrequent emails
        </label>
        <p class="help-block">
          Some examples: important new features and "What feature should I build next?"-type questions. No spam or other bullshit.
        </p>
      </div>
      <button
        class="btn btn-primary btn-lg btn-block"
        v-bind:disabled="registering"
        v-on:click.prevent="registerWithEmail()"
      >
        {{ registerText }}
      </button>
    </form>
    <hr>
    <p>
      <a class="btn btn-social btn-google btn-lg btn-block" v-bind:href="googleRegistrationUrl" v-bind:disabled="registering">
        <i class="fab fa-google"></i>
        Register with Google
      </a>
    </p>
    <p>
      <a class="btn btn-social btn-twitter btn-lg btn-block" v-bind:href="twitterRegistrationUrl" v-bind:disabled="registering">
        <i class="fab fa-twitter"></i>
        Register with Twitter
      </a>
    </p>
    <p>
      <a class="btn btn-social btn-linkedin btn-lg btn-block" v-bind:href="linkedinRegistrationUrl" v-bind:disabled="registering">
        <i class="fab fa-linkedin"></i>
        Register with LinkedIn
      </a>
    </p>
    <hr>
    <p>Already have an account? <router-link to="/sign-in">Sign in here</router-link>.</p>
  </section>
</template>

<script>
import ErrorsAlert from '@/components/utilities/errors-alert';
import FiveCardHands from '@/services/five-card-hands/FiveCardHands';

export default {
  name: 'ppt-register',
  components: {
    'errors-alert': ErrorsAlert,
  },
  data: function () {
    return {
      email: '',
      password: '',
      emailOptIn: false,
      xkcdVisible: false,
      submitAttempted: false,
      errorsFromBackend: [],
      registering: false,
    };
  },
  computed: {
    anyErrors: function () {
      return !!(this.emailErrorMessage || this.passwordErrorMessage);
    },
    emailErrorMessage: function () {
      if (this.email === '') {
        return 'An email is required';
      } else if (!this.emailValid) {
        return 'Invalid email';
      } else {
        return false;
      }
    },
    emailValid: function () {
      let regex = /\S+@\S+\.\S+/;
      return regex.test(this.email);
    },
    passwordErrorMessage: function () {
      if (this.password === '') {
        return 'A password is required';
      } else if (this.password.length < 8) {
        return 'Must be at least eight characters';
      } else {
        return false;
      }
    },
    googleRegistrationUrl: function () {
      return `${process.env.BASE_API_URL}/user/google`;
    },
    twitterRegistrationUrl: function () {
      return `${process.env.BASE_API_URL}/user/twitter`;
    },
    linkedinRegistrationUrl: function () {
      return `${process.env.BASE_API_URL}/user/linkedin`;
    },
    registerText: function () {
      if (this.registering) {
        return 'Registering...';
      }

      return 'Register';
    },
    defaultIncludedByDefault: function () {
      let fiveCardHands = new FiveCardHands();

      return fiveCardHands.defaultIncludedByDefault;
    },
    defaultCollapsedByDefault: function () {
      let fiveCardHands = new FiveCardHands();

      return fiveCardHands.defaultCollapsedByDefault;
    },
  },
  methods: {
    registerWithEmail: function () {
      let self = this;

      if (self.anyErrors) {
        self.submitAttempted = true;
        return;
      }

      self.registering = true;
      self.$http
        .post(`${process.env.BASE_API_URL}/user`, {
          user: {
            email: self.email,
            password: self.password,
            emailOptIn: self.emailOptIn,
          },
          defaultIncludedByDefault: self.defaultIncludedByDefault,
          defaultCollapsedByDefault: self.defaultCollapsedByDefault,
        })
        .then(function (res) {
          self.registering = false;
          self.$store.commit('account/setUser', res.data.user);
          self.$router.push({ path: '/account' });
          alert('Successfully registered!');
        })
        .catch(function (err) {
          self.registering = false;
          self.errorsFromBackend.splice(0, self.errorsFromBackend.length);
          err.response.data.errors.forEach(function (errorFromBackend) {
            self.errorsFromBackend.push(errorFromBackend);
          });
        })
      ;
    },
    clearAlert: function () {
      this.errorsFromBackend.splice(0, this.errorsFromBackend.length);
    },
  },
};
</script>

<style lang="scss" scoped>
  section, input, button, a {
    font-size: 18px;
  }

  img {
    max-width: 100%;
    display: block;
    margin-bottom: 20px;
  }

  label {
    width: 100%;
  }

  .btn-social {
    text-align: center;
  }

  .xkcd-toggle {
    cursor: pointer;
    text-decoration: underline;
  }
</style>



// WEBPACK FOOTER //
// src/components/user/register.vue